import { type FC, useRef, useEffect, useState } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconCircleCheckedFilled } from "@/assets/icons/central/IconCircleCheckedFilled";
import { IconCircle } from "@/assets/icons/geist/IconCircle";
import { OnaThinking } from "@/components/ona/OnaThinking";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { AgentMessageFragment, AgentMessageFragmentType } from "@/hooks/use-stream-conversation";
import type { AgentTodoGroupItem } from "@/utils/message-grouping";
import { Fragment } from "@/components/ai/Fragment";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { AgentExecutionState, agentExecutionState } from "@/utils/agent-executions";
import { OnaWaiting } from "@/components/ona/OnaWaiting";

export type TodoContainerProps = {
    todo: AgentTodoGroupItem;
    isLastGroup: boolean;
    isLastTodoInGroup: boolean;
    agentExecution: PlainAgentExecution;
    onClick?: (block: AgentMessageFragment) => void;
};

export const TodoContainer: FC<TodoContainerProps> = ({
    todo,
    isLastGroup: _isLastGroup,
    isLastTodoInGroup,
    agentExecution,
    onClick,
}) => {
    const { todoId, todoTitle, todoStatus, fragments } = todo;
    const fragmentCount = fragments.length;

    const [isOpen, setIsOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const agentState = agentExecutionState(agentExecution);
    const isAgentWorking = agentState === AgentExecutionState.WORKING;

    const isPending = todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING;
    const isInProgress = todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
    const isCompleted = todoStatus === AgentResponseBlock_TodoItem_Phase.DONE;
    const isCleared = todoStatus === AgentResponseBlock_TodoItem_Phase.CLEARED;
    const hasContent = fragments.length > 0;

    // Auto-scroll to bottom when in progress and is last
    useEffect(() => {
        if (isInProgress && isLastTodoInGroup && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    });

    const getStatusIcon = () => {
        if (isPending || isCleared) {
            return <IconCircle size="sm" className="size-5 flex-shrink-0 text-content-muted" />;
        } else if (isCompleted) {
            return <IconCircleCheckedFilled className={cn("size-5 text-content-success")} />;
        } else if (isInProgress && isAgentWorking) {
            return <OnaThinking state="thinking" size="sm" className="size-5" />;
        } else if (isInProgress && !isAgentWorking) {
            return <OnaWaiting cycleInterval={1000} size="sm" className="size-[17px]" />;
        }
        return null;
    };

    const getStatusLabel = () => {
        if (isCompleted) return "Completed";
        if (isInProgress) return "In progress";
        if (isCleared) return "Cleared";
        return "Pending";
    };

    return (
        <div
            id={`todo-${todoId}`}
            className={cn("border-b border-border-base transition-colors last:border-b-0", {
                "border-transparent": isPending,
            })}
        >
            {/* Todo Header */}
            <button
                onClick={() => {
                    const canExpand = hasContent && (fragmentCount === undefined || fragmentCount > 0);
                    if (canExpand) setIsOpen(!isOpen);
                }}
                className={cn(
                    "flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-all duration-300 ease-out",
                    hasContent && (fragmentCount === undefined || fragmentCount > 0) && "hover:bg-surface-muted",
                    (!hasContent || (fragmentCount !== undefined && fragmentCount === 0)) && "cursor-default",
                )}
                aria-expanded={hasContent && (fragmentCount === undefined || fragmentCount > 0) ? isOpen : undefined}
                aria-label={`${getStatusLabel()}: ${todoTitle}`}
                data-tracking-id="todo-container-toggle"
                disabled={!hasContent || (fragmentCount !== undefined && fragmentCount === 0)}
            >
                {/* Left side: Status icon + Text content (title + fragment preview) */}
                <div
                    className={cn("flex min-w-0 flex-1 items-center gap-2", {
                        "opacity-50": isPending || isCleared,
                    })}
                >
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{getStatusIcon()}</div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0">
                        {/* Title */}
                        {isInProgress && isAgentWorking ? (
                            <TextShimmer className="min-w-0 truncate text-base text-content-primary">
                                {todoTitle}
                            </TextShimmer>
                        ) : (
                            <h2
                                className={cn(
                                    "min-w-0 truncate text-base text-content-primary transition-colors duration-300",
                                    {
                                        "text-content-muted": isCompleted || isPending || isCleared,
                                        "line-through": isCleared,
                                    },
                                )}
                            >
                                {todoTitle}
                            </h2>
                        )}

                        {/* Progress */}
                        <Collapsible open={isInProgress}>
                            <CollapsibleContent>
                                <TextShimmer className="min-w-0 truncate font-mono text-sm" shimmer={isAgentWorking}>
                                    {currentFragmentText(todo)}
                                </TextShimmer>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                </div>

                {/* Right side: Controls (chevron only) - vertically centered */}
                <div className="flex flex-shrink-0 items-center">
                    <IconChevronDown
                        size="sm"
                        aria-hidden={isPending}
                        className={cn(
                            "flex-shrink-0 opacity-100 transition-[opacity,transform] duration-200",
                            {
                                "opacity-0": isPending,
                                "opacity-40": !isPending && (!hasContent || isCleared),
                                "cursor-not-allowed": !hasContent,
                            },
                            isOpen && "rotate-180",
                        )}
                    />
                </div>
            </button>

            {/* Todo Content - Simple version without complex animations */}
            {isOpen && hasContent && (
                <div
                    ref={scrollContainerRef}
                    className={cn(
                        "w-full overflow-y-auto border-t border-border-base px-4 py-2 text-base",
                        "max-h-[40vh]",
                    )}
                >
                    <div className="flex flex-col gap-4">
                        {todo.fragments.map((fragment, fragIndex) => {
                            return (
                                <Fragment
                                    key={`${todo.todoId}-${fragIndex}`}
                                    fragment={fragment}
                                    agentExecution={agentExecution}
                                    shouldHighlightFragment={false}
                                    onClick={onClick}
                                    data-tracking-id="default-tracking-id"
                                    ignore={IgnoreFragments}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// Todo items are handled at the grouping level, so we don't
// want to render them as fragments
const IgnoreFragments = new Set<AgentMessageFragmentType>(["todoItem"]);

function currentFragmentText(todo: AgentTodoGroupItem) {
    const currentFragment = todo.fragments.length > 0 ? todo.fragments[todo.fragments.length - 1] : null;
    let text = "Thinking...";
    if (currentFragment && currentFragment.type === "text" && currentFragment.content) {
        text = currentFragment.content.split("\n")[0].substring(0, 100);
    } else if (currentFragment && currentFragment.type === "action" && currentFragment.intentDescription) {
        text = currentFragment.intentDescription;
    }
    return text;
}
